import { apiClient } from "@/lib/api/client";
import { buildQueryString } from "@/lib/utils/query-string";

const BASE = "/api/users/";

// Password management
export async function updatePassword(payload: Record<string, unknown>) {
  const { data } = await apiClient.put(`${BASE}password/update/`, payload);
  return data;
}

export async function changePassword(payload: Record<string, unknown>) {
  const { data } = await apiClient.patch(`${BASE}password/change/`, payload);
  return data;
}

export async function forgotPassword(payload: Record<string, unknown>) {
  const { data } = await apiClient.post(`${BASE}password/forget/`, payload);
  return data;
}

// Business users
export async function getBusinessUsers() {
  const { data } = await apiClient.get(`${BASE}business_user/`);
  return data;
}

export async function addBusinessUser(payload: Record<string, unknown>) {
  const { data } = await apiClient.post(`${BASE}business_user/`, payload);
  return data;
}

export async function updateBusinessUser(id: number, payload: Record<string, unknown>) {
  const { data } = await apiClient.patch(`${BASE}business_user/${id}/`, payload);
  return data;
}

export async function changeBusinessManager(id: number) {
  const { data } = await apiClient.patch(`${BASE}business_user/${id}/make_manager/`);
  return data;
}

// Staff users
export async function getStaffUsers() {
  const { data } = await apiClient.get(`${BASE}staff/`);
  return data;
}

export async function addStaffUser(payload: Record<string, unknown>) {
  const { data } = await apiClient.post(`${BASE}staff/`, payload);
  return data;
}

export async function updateStaffUser(id: number, payload: Record<string, unknown>) {
  const { data } = await apiClient.patch(`${BASE}staff/${id}/`, payload);
  return data;
}

// Registration
export async function register(payload: Record<string, unknown>, accountType: "P" | "B") {
  const url = accountType === "B" ? `${BASE}register/business/` : `${BASE}register/`;
  const { data } = await apiClient.post(url, payload);
  return data;
}

// Customers & Companies
export async function getCustomers() {
  const { data } = await apiClient.get(`${BASE}customers/`);
  return data;
}

export async function getPersonalAccounts(params: Record<string, unknown>) {
  const query = buildQueryString(params);
  const { data } = await apiClient.get(`${BASE}personal/?${query}`);
  return data;
}

export async function getCompanies(params: Record<string, unknown>) {
  const query = buildQueryString(params);
  const { data } = await apiClient.get(`${BASE}companies/?${query}`);
  return data;
}

export async function getCompany(id: number) {
  const { data } = await apiClient.get(`${BASE}companies/${id}/`);
  return data;
}

export async function getUserProfile(id: number) {
  const { data } = await apiClient.get(`${BASE}customers/${id}/`);
  return data;
}

export async function getMyPaymentOptions() {
  const { data } = await apiClient.get(`${BASE}me/options/`);
  return data;
}

export async function getMyDefaultFilters() {
  const { data } = await apiClient.get(`${BASE}me/filters/`);
  return data;
}

export async function updateMyDefaultFilters(payload: Record<string, unknown>) {
  const { data } = await apiClient.patch(`${BASE}me/filters/`, payload);
  return data;
}

// Credit
export async function getCustomerCreditLog(customerId: number, params: Record<string, unknown>) {
  const query = buildQueryString(params);
  const { data } = await apiClient.get(`${BASE}customers/${customerId}/credit/?${query}`);
  return data;
}

export async function addCustomerCreditMemo(customerId: number, payload: Record<string, unknown>) {
  const { data } = await apiClient.post(`${BASE}customers/${customerId}/credit/`, payload);
  return data;
}

export async function getCompanyCreditLog(companyId: number, params: Record<string, unknown>) {
  const query = buildQueryString(params);
  const { data } = await apiClient.get(`${BASE}companies/${companyId}/credit/?${query}`);
  return data;
}

export async function addCompanyCreditMemo(companyId: number, payload: Record<string, unknown>) {
  const { data } = await apiClient.post(`${BASE}companies/${companyId}/credit/`, payload);
  return data;
}

export async function reverseCreditMemo(id: number, notes: string) {
  const { data } = await apiClient.patch(`${BASE}credit_memo/${id}/reverse/`, { notes });
  return data;
}

// Addresses
export async function listAddresses() {
  const { data } = await apiClient.get(`${BASE}address/`);
  return data;
}

export async function createAddress(payload: Record<string, unknown>) {
  const { data } = await apiClient.post(`${BASE}address/`, payload);
  return data;
}

export async function deleteAddress(id: number) {
  const { data } = await apiClient.delete(`${BASE}address/${id}/`);
  return data;
}

export async function createAddressForCustomer(customerId: number, payload: Record<string, unknown>) {
  const { data } = await apiClient.post(`${BASE}customer/${customerId}/address/`, payload);
  return data;
}

export async function getCustomerAddresses(customerId: number) {
  const { data } = await apiClient.get(`${BASE}customer/${customerId}/address/`);
  return data;
}

// Invitations
export async function sendUserInvitation(payload: Record<string, unknown>) {
  const { data } = await apiClient.post(`${BASE}invite/`, payload);
  return data;
}

export async function acceptInvitation(invitationId: string) {
  const { data } = await apiClient.post(`${BASE}invite/${invitationId}/accept/`);
  return data;
}

// Request access
export async function sendRequestAccess(payload: Record<string, unknown>) {
  const { data } = await apiClient.post(`${BASE}request_access/send/`, payload);
  return data;
}

export async function getRequestAccessList(params: Record<string, unknown>) {
  const query = buildQueryString(params);
  const { data } = await apiClient.get(`${BASE}request_access/?${query}`);
  return data;
}

export async function rejectRequestAccess(requestId: number) {
  const { data } = await apiClient.patch(`${BASE}request_access/${requestId}/reject/`);
  return data;
}

// Reminders
export async function sendPickupReminder(id: number, isCompany: boolean) {
  const path = isCompany ? `companies/${id}` : `customers/${id}`;
  const { data } = await apiClient.post(`${BASE}${path}/send_pickup_reminder/`);
  return data;
}

export async function sendPaymentReminder(id: number, isCompany: boolean) {
  const path = isCompany ? `companies/${id}` : `customers/${id}`;
  const { data } = await apiClient.post(`${BASE}${path}/send_payment_reminder/`);
  return data;
}

// Overdue interest & Storage fees
export async function updateOverdueInterestSettings(id: number, isCompany: boolean, payload: Record<string, unknown>) {
  const path = isCompany ? `companies/${id}` : `customers/${id}`;
  const { data } = await apiClient.patch(`${BASE}${path}/overdue_interest/`, payload);
  return data;
}

export async function updateStorageFeesSettings(id: number, isCompany: boolean, payload: Record<string, unknown>) {
  const path = isCompany ? `companies/${id}` : `customers/${id}`;
  const { data } = await apiClient.patch(`${BASE}${path}/storage_fees/`, payload);
  return data;
}
